import Image from "next/image";

import cn from "classnames";
import type { ReactNode } from "react";

import { ButtonLink } from "@/components/buttonLink/buttonLink.component";
import { CheckmarkThinIcon } from "@/components/icons/checkmarkThin.icon";
import { CloseIcon } from "@/components/icons/close.icon";
import { SaleIcon } from "@/components/icons/sale.icon";
import { Price } from "@/components/price/price.component";
import { SalesPrice } from "@/components/salesPrice/salesPrice.component";
import { Text } from "@/components/text/text.component";

import { urlFor } from "@/sanity/utils";

import type { ProductCardListingCardData } from "./productCardListing.component";

const FOOTNOTE_MARK = "†";

/** Renders † as superscript in plain-text feature labels, like withRegisteredTrademark. */
const withFootnoteMarks = (text: string): ReactNode => {
  if (!text.includes(FOOTNOTE_MARK)) return text;

  const parts = text.split(FOOTNOTE_MARK);
  return parts.flatMap((part, index) =>
    index < parts.length - 1
      ? [
          part,
          <sup
            key={`${index}-footnote`}
            className="align-super text-[0.6em] leading-none">
            {FOOTNOTE_MARK}
          </sup>,
        ]
      : [part],
  );
};

export const ProductCardListingCard = ({
  card,
  marker = "check",
}: ProductCardListingCardProps) => {
  const imageUrl = card.image?.asset
    ? urlFor(card.image).width(800).url()
    : null;
  const features = card.features ?? [];

  return (
    <article
      data-testid="product-card-listing-card"
      className="flex h-full flex-col gap-6">
      {imageUrl ? (
        <div className="bg-greyscale-background relative h-64 w-full overflow-hidden rounded-2xl lg:h-80">
          <Image
            src={imageUrl}
            alt={card.image?.alt || ""}
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
            className="object-contain p-6"
          />
        </div>
      ) : null}

      <div className="flex flex-1 flex-col gap-6 px-6">
        <div className="flex flex-col gap-2">
          {card.eyebrow ? (
            <Text
              variant="preHeaderSm"
              component="p"
              className="text-secondary-100 tracking-[0.04em] uppercase">
              {card.eyebrow}
            </Text>
          ) : null}
          <Text variant="h5" component="h3" className="text-greyscale-headline">
            {card.title}
          </Text>
        </div>

        {card.promoBadge ? (
          <div
            data-testid="product-card-listing-promo-badge"
            className="bg-adt-amber text-secondary inline-flex items-center gap-2 self-start rounded px-3 py-1">
            <SaleIcon width={14} height={14} aria-hidden />
            <Text variant="body5" component="span" className="uppercase">
              {card.promoBadge}
            </Text>
          </div>
        ) : null}

        {card.promoText ? (
          <Text
            variant="body2"
            component="p"
            data-testid="product-card-listing-promo-text"
            className="text-success">
            {card.promoText}
          </Text>
        ) : null}

        {card.description ? (
          <Text variant="body1" component="p" className="text-greyscale-body">
            {card.description}
          </Text>
        ) : null}

        {features.length ? (
          <ul
            data-marker={marker}
            className="border-greyscale-muted flex flex-col gap-4 border-y py-6">
            {features.map((feature) => {
              // Bullets ignore the "Included" toggle.
              const included =
                marker === "bullet" || feature.included !== false;
              return (
                <li
                  key={feature._key}
                  data-included={included}
                  className="flex items-start gap-3">
                  {marker === "bullet" ? (
                    <span
                      aria-hidden="true"
                      className="flex h-6 w-6 shrink-0 items-center justify-center">
                      <span className="bg-greyscale-strength h-1.5 w-1.5 rounded-full" />
                    </span>
                  ) : included ? (
                    <CheckmarkThinIcon
                      width={24}
                      height={24}
                      aria-hidden
                      className="text-greyscale-body shrink-0"
                    />
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                      <CloseIcon
                        width={18}
                        height={18}
                        className="text-greyscale-muted"
                      />
                    </span>
                  )}
                  {!included && <span className="sr-only">Not included:</span>}
                  <Text
                    variant={marker === "bullet" ? "body2" : "body4"}
                    component="span"
                    className={cn(
                      "pt-0.5",
                      included ? "text-greyscale-body" : "text-greyscale-muted",
                    )}>
                    {withFootnoteMarks(feature.label)}
                  </Text>
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="flex flex-col gap-1">
          {card.billingLabel ? (
            <Text
              variant="subtitle2"
              component="p"
              className="text-greyscale-headline">
              {card.billingLabel}
            </Text>
          ) : null}
          <SalesPrice
            salesPrice={card.salesPrice}
            listPrice={card.listPrice}
            monthly
            className="items-end!"
            listPriceClassName="body-6 text-greyscale-body line-through whitespace-nowrap"
          />
          {card.activationFee != null ? (
            <div
              data-testid="product-card-listing-activation-fee"
              className="body-6 text-greyscale-body flex flex-wrap items-end gap-1">
              <span>+ {card.activationFeeLabel || "Activation fee"}:</span>
              {/* Price treats 0 as empty, and its `free` state is always green; Figma shows FREE in body text. */}
              {card.activationFee === 0 ? (
                <span>FREE</span>
              ) : (
                <Price value={card.activationFee} />
              )}
              {card.originalActivationFee != null ? (
                <Price
                  value={card.originalActivationFee}
                  className="text-xs line-through"
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <ButtonLink
          cta={{ ctaLabel: card.ctaLabel, ctaLink: card.ctaLink }}
          theme="secondary"
          prefetch={false}
          className="mt-auto w-full normal-case! md:w-max"
        />
      </div>
    </article>
  );
};

export type ProductCardListingCardProps = {
  card: ProductCardListingCardData;
  marker?: "check" | "bullet";
};
